import { useState } from 'react';
import { useApi, type ToolConfig } from '@/lib/api';
import { useToast } from '@/components/Toast';
import { Plus, Trash2, Beaker } from 'lucide-react';

const DEFAULT_SCHEMA = `{
  "type": "object",
  "properties": {
    "email": { "type": "string", "description": "Customer email" }
  },
  "required": ["email"]
}`;

export function CustomToolForm(props: {
  orgId: string;
  tools: ToolConfig[];
  onChanged: () => void;
}) {
  const api = useApi();
  const toast = useToast();
  const [form, setForm] = useState({
    name: '',
    description: '',
    method: 'POST' as 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE',
    url: '',
    authHeaderName: 'Authorization',
    authHeaderValue: '',
    inputSchema: DEFAULT_SCHEMA,
    needsApproval: false,
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const customs = props.tools.filter((t) => t.source === 'custom_http');

  const create = async () => {
    setErr(null);
    let schema: unknown;
    try { schema = JSON.parse(form.inputSchema); }
    catch (e) { setErr(`invalid JSON schema: ${(e as Error).message}`); return; }
    setBusy(true);
    try {
      await api.post(`/api/orgs/${props.orgId}/tools/custom`, {
        name: form.name,
        description: form.description,
        method: form.method,
        url: form.url,
        authHeaderName: form.authHeaderName || undefined,
        authHeaderValue: form.authHeaderValue || undefined,
        inputSchema: schema,
        needsApproval: form.needsApproval,
      });
      setForm({ ...form, name: '', description: '', url: '', authHeaderValue: '' });
      props.onChanged();
      toast.success('Custom tool created');
    } catch (e) {
      const msg = (e as Error).message;
      setErr(msg);
      toast.error('Failed to create tool', msg);
    } finally {
      setBusy(false);
    }
  };

  const remove = async (toolId: string) => {
    const id = toolId.replace(/^custom_http\./, '');
    if (!confirm('Delete this custom tool?')) return;
    try {
      await api.delete(`/api/orgs/${props.orgId}/tools/custom/${id}`);
      props.onChanged();
      toast.success('Custom tool deleted');
    } catch (e) {
      toast.error('Delete failed', (e as Error).message);
    }
  };

  return (
    <div className="grid lg:grid-cols-2 gap-6 max-w-5xl">
      <section className="card p-5 space-y-3">
        <h3 className="font-semibold">Add a custom HTTP tool</h3>
        <p className="text-sm text-muted-foreground">
          Expose one of your own APIs as a tool your AI agent can call — e.g. `lookup_customer`.
          Auth header values are encrypted at rest with <code>TOOL_AES_KEY</code>.
        </p>
        <label className="block text-sm">
          Name
          <input className="input mt-1"
                 placeholder="lookup_customer"
                 value={form.name}
                 onChange={(e) => setForm({ ...form, name: e.target.value })} />
        </label>
        <label className="block text-sm">
          Description
          <input className="input mt-1"
                 placeholder="Look up a customer by email and return plan + status."
                 value={form.description}
                 onChange={(e) => setForm({ ...form, description: e.target.value })} />
        </label>
        <div className="grid grid-cols-[100px_1fr] gap-2">
          <label className="block text-sm">
            Method
            <select className="input mt-1"
                    value={form.method}
                    onChange={(e) => setForm({ ...form, method: e.target.value as typeof form.method })}>
              {['GET', 'POST', 'PUT', 'PATCH', 'DELETE'].map(m => <option key={m}>{m}</option>)}
            </select>
          </label>
          <label className="block text-sm">
            URL
            <input className="input mt-1"
                   placeholder="https://api.acme.com/customers/lookup"
                   value={form.url}
                   onChange={(e) => setForm({ ...form, url: e.target.value })} />
          </label>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <label className="block text-sm">
            Auth header name
            <input className="input mt-1"
                   placeholder="Authorization"
                   value={form.authHeaderName}
                   onChange={(e) => setForm({ ...form, authHeaderName: e.target.value })} />
          </label>
          <label className="block text-sm">
            Auth header value <span className="text-muted-foreground">(stored encrypted)</span>
            <input className="input mt-1"
                   type="password"
                   placeholder="Bearer sk_live_…"
                   value={form.authHeaderValue}
                   onChange={(e) => setForm({ ...form, authHeaderValue: e.target.value })} />
          </label>
        </div>
        <label className="block text-sm">
          Input JSON schema
          <textarea className="input mt-1 min-h-[160px] font-mono text-xs"
                    value={form.inputSchema}
                    onChange={(e) => setForm({ ...form, inputSchema: e.target.value })} />
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox"
                 checked={form.needsApproval}
                 onChange={(e) => setForm({ ...form, needsApproval: e.target.checked })} />
          Require human approval before every call
        </label>
        {err && <p className="text-sm text-destructive">{err}</p>}
        <button className="btn-primary" disabled={busy || !form.name || !form.url} onClick={create}>
          <Plus className="h-3.5 w-3.5" />
          {busy ? 'Saving…' : 'Create tool'}
        </button>
      </section>

      <section>
        <h3 className="font-semibold mb-3">Existing custom tools</h3>
        {customs.length === 0 ? (
          <p className="text-sm text-muted-foreground">None yet.</p>
        ) : (
          <ul className="space-y-2">
            {customs.map((t) => (
              <li key={t.id} className="card p-3 flex items-center justify-between gap-3">
                <div className="text-sm font-mono truncate">{t.tool_id}</div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    className="btn-ghost"
                    title="Smoke test"
                    aria-label={`Test ${t.tool_id}`}
                    onClick={async () => {
                      const id = t.tool_id.replace(/^custom_http\./, '');
                      try {
                        const data = await api.post(
                          `/api/orgs/${props.orgId}/tools/custom/${id}/test`,
                          {},
                        );
                        toast.success(
                          'Test succeeded',
                          JSON.stringify(data).slice(0, 200),
                        );
                      } catch (e) {
                        toast.error('Test failed', (e as Error).message);
                      }
                    }}
                  >
                    <Beaker className="h-3.5 w-3.5" />
                    Test
                  </button>
                  <button
                    className="btn-ghost text-destructive"
                    onClick={() => remove(t.tool_id)}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
